/**
 * Types + pure helpers for the GENERALISED fixed-route vehicle mapping panel
 * (`VehicleMappingPanel`).
 *
 * This module is deliberately decoupled from the Postcode/ELC flow. It reuses
 * the existing `OpsVehicle` / `VehicleSlot` shapes from the postcode-assignment
 * service layer (the Ops read path and slot model are identical), but the
 * preset mechanism here is fully pluggable: ELC DAY/LATE/NIGHT is just one
 * optional preset a caller can pass in, not a baked-in default.
 */

import type { OpsVehicle, VehicleSlot } from '../postcodeAssignment/opsVehicleTypes';

export type { OpsVehicle, VehicleSlot } from '../postcodeAssignment/opsVehicleTypes';

/**
 * A pluggable slot preset. Callers (e.g. the fixed-route deploy flow) supply
 * presets to seed the slot list with a meaningful starting point — e.g. one
 * slot per route, or ELC shift bands. The panel renders one button per preset.
 */
export interface VehicleSlotPreset {
  /** Stable id used as the React key and the apply handle. */
  id: string;
  /** Button label, e.g. "ELC bands" or "One per route". */
  label: string;
  /** Optional longer description shown as a tooltip. */
  description?: string;
  /** Produces the slots this preset seeds. Called fresh each apply. */
  build: () => VehicleSlot[];
}

/**
 * Builds a stable-ish local id for a slot. Mirrors the convention used by the
 * postcode panel so downstream readers treat slots from either source the same.
 */
export function makeSlotLocalId(prefix = 'slot'): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Builds a single empty, unmapped slot. */
export function buildEmptyVehicleSlot(name = ''): VehicleSlot {
  return {
    localId: makeSlotLocalId(),
    name,
    startWindow: '',
    endWindow: '',
    opsVehicleId: '',
    opsVehicleLabel: '',
  };
}

/**
 * The identity key a slot is persisted under in the interim mapping store.
 *
 * We key on the slot's `localId`. (PRD §7.2 frames the store as
 * `cartographerVehicleId | slotId → opsVehicleId`; for the generalised panel a
 * slot is the unit being mapped, so its `localId` is the natural key.)
 */
export function mappingKeyForSlot(slot: Pick<VehicleSlot, 'localId'>): string {
  return slot.localId;
}

/** Normalises a free-text identifier for tolerant comparison (case/space/punct). */
function normaliseToken(value: string | undefined | null): string {
  return (value ?? '')
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s\-_.]+/g, '');
}

export interface AutoMatchResult {
  /** Slots with their `opsVehicleId`/`opsVehicleLabel` filled where a match was found. */
  slots: VehicleSlot[];
  /** Count of slots that were newly matched by this pass. */
  matchedCount: number;
  /** Slot `localId`s that remain unmapped after the pass. */
  unmatchedSlotIds: string[];
}

/**
 * Builds a human-readable Ops vehicle label (rego / label, with capacity).
 * Mirrors the postcode panel's display convention so the two stay consistent.
 */
export function describeOpsVehicle(vehicle: OpsVehicle): string {
  const base = vehicle.rego || vehicle.label || vehicle.id;
  return vehicle.seatingCapacity ? `${base} (${vehicle.seatingCapacity})` : base;
}

/**
 * Auto-matches slots to Ops vehicles by comparing the slot name against each
 * vehicle's rego, label, and id (the PRD asks for rego/name/uatVehicleId here;
 * `OpsVehicle.id` is the Ops vehicle uuid and rego/label cover the human names).
 *
 * Pure and side-effect free so it can be unit tested. Only fills slots that are
 * currently unmapped; already-mapped slots are left untouched. Each Ops vehicle
 * is consumed at most once so two slots never auto-claim the same vehicle.
 */
export function autoMatchSlotsToVehicles(
  slots: VehicleSlot[],
  vehicles: OpsVehicle[],
): AutoMatchResult {
  const claimed = new Set<string>();
  // Seed claimed with vehicles already mapped on incoming slots so auto-match
  // does not re-hand an in-use vehicle to a different slot.
  for (const slot of slots) {
    if (slot.opsVehicleId) claimed.add(slot.opsVehicleId);
  }

  let matchedCount = 0;
  const unmatchedSlotIds: string[] = [];

  const nextSlots = slots.map((slot) => {
    if (slot.opsVehicleId) return slot; // keep existing mapping
    // Match on the route's OWN vehicle (sim rego/name) when known, else the
    // slot name. This binds "the vehicle the route used" to the Ops vehicle.
    const target = normaliseToken(slot.vehicleMatchHint || slot.name);
    if (!target) {
      unmatchedSlotIds.push(slot.localId);
      return slot;
    }
    const match = vehicles.find((vehicle) => {
      if (claimed.has(vehicle.id)) return false;
      return (
        normaliseToken(vehicle.rego) === target ||
        normaliseToken(vehicle.label) === target ||
        normaliseToken(vehicle.id) === target
      );
    });
    if (!match) {
      unmatchedSlotIds.push(slot.localId);
      return slot;
    }
    claimed.add(match.id);
    matchedCount += 1;
    return {
      ...slot,
      opsVehicleId: match.id,
      opsVehicleLabel: describeOpsVehicle(match),
    };
  });

  return { slots: nextSlots, matchedCount, unmatchedSlotIds };
}
